/**
 * Period rules from the product contract:
 *  - the current calendar month is the Open Plan;
 *  - the latest completed unpublished month is the Close Month target;
 *  - published months are reporting history.
 * All dates are local (Africa/Lagos for the owner).
 */
export interface PeriodRow {
  month_start: string;
  status: 'draft' | 'review' | 'published';
}

const pad = (n: number) => String(n).padStart(2, '0');

export function monthStartOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-01`;
}

export function addMonths(monthStart: string, n: number): string {
  const [y, m] = monthStart.split('-').map(Number) as [number, number];
  const d = new Date(y, m - 1 + n, 1);
  return monthStartOf(d);
}

export const monthKey = (monthStart: string) => monthStart.slice(0, 7);
export const monthStartFromKey = (key: string) => `${key}-01`;

export function currentMonthStart(now = new Date()): string {
  return monthStartOf(now);
}

/** Published months, newest first. */
export function publishedMonths<T extends PeriodRow>(months: T[]): T[] {
  return months.filter((m) => m.status === 'published').sort((a, b) => b.month_start.localeCompare(a.month_start));
}

export function latestPublished<T extends PeriodRow>(months: T[]): T | null {
  return publishedMonths(months)[0] ?? null;
}

/**
 * The month Close Month should work on, or null when everything completed is published.
 * Prefers last month; if it is published, falls back to the newest older unpublished month.
 */
export function closeTargetMonth(months: PeriodRow[], now = new Date()): string | null {
  const previous = addMonths(currentMonthStart(now), -1);
  const row = months.find((m) => m.month_start === previous);
  if (!row || row.status !== 'published') return previous;
  const older = months
    .filter((m) => m.month_start < previous && m.status !== 'published')
    .sort((a, b) => b.month_start.localeCompare(a.month_start));
  return older[0]?.month_start ?? null;
}

/** The published month immediately before the given one, for comparisons. */
export function previousPublished<T extends PeriodRow>(months: T[], monthStart: string): T | null {
  return publishedMonths(months).find((m) => m.month_start < monthStart) ?? null;
}
